import { useTheme } from "@emotion/react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { AVATAR } from "../../lib/constants.js";
import { formatStatusText } from "../../lib/formatStatusText.js";
import { computeStats } from "../../lib/stats.js";
import { useUser } from "../UserContext.js";
import DataBoxItemRow from "./DataBoxItemRow.js";
import DataBoxStats from "./DataBoxStats.js";

export default function DataBox({ character, host, locales }) {
  const { isHost, runId } = useUser();
  const { palette } = useTheme();
  const { concept, id: characterId, involved = [], name } = character || {};
  const { id: hostId, serial, status, user } = host || {};

  const stats = useMemo(() => computeStats({ host, locales }), [host, locales]);

  return (
    <Paper
      id="data-box"
      sx={{
        boxShadow: `0 0 5px ${palette.divider}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
      }}
    >
      <Avatar
        alt={`${name} avatar`}
        src={user?.imageUrl}
        sx={{
          alignSelf: "center",
          boxShadow: `0 0 5px ${palette.divider}`,
          width: { xs: AVATAR.SIZE.SM, md: AVATAR.SIZE.LG },
          height: { xs: AVATAR.SIZE.SM, md: AVATAR.SIZE.LG },
        }}
      />
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="body1" fontStyle="italic">
          {concept}
        </Typography>
        <DataBoxItemRow
          id="data-box-name"
          isHost={isHost}
          route={characterId ? `/${runId}/plots/characters/${characterId}` : null}
          state={{ id: characterId }}
          title={locales["character.name"]}
          value={name}
        />
        <DataBoxItemRow
          id="data-box-host"
          isHost={isHost}
          route={hostId ? `/${runId}/security/hosts/${hostId}` : null}
          state={{ id: hostId }}
          title={locales["character.currentHost"]}
          value={serial}
        />
        <DataBoxItemRow
          id="data-box-status"
          isHost={isHost}
          route={hostId ? `/${runId}/maintenance/${hostId}` : null}
          state={{ id: hostId }}
          title={locales["character.status"]}
          value={formatStatusText({ locales, status })}
        />
        {isHost ? null : (
          <>
            <DataBoxStats
              locales={locales}
              route={`/${runId}/behaviour/${hostId}`}
              state={{ id: hostId }}
              stats={stats}
              title={locales["character.stats"]}
            />
            <Box>
              <Typography
                color="secondary"
                variant="body2"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                {locales["character.plots"]}
              </Typography>
              <List dense disablePadding>
                {!involved.length ? (
                  <Typography variant="body2">
                    {locales["character.noPlots"]}
                  </Typography>
                ) : (
                  involved.map((involvement, index) => (
                    <ListItem
                      component={Link}
                      disableGutters
                      key={index}
                      state={{ id: involvement.plot.id }}
                      to={`/${runId}/plots/plots/${involvement.plot.id}`}
                    >
                      <ListItemText primary={involvement.plot.name} />
                    </ListItem>
                  ))
                )}
              </List>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
}
